/**
 * Which editor a catalog file opens in, decided by its extension.
 *
 * The backend is deliberately format-agnostic -- it treats any path segment
 * containing a dot as a file, and its storage, permission, rename and locking
 * routes never look at the extension. Only the Azure DevOps publishing pipeline
 * filters on ".bpmn", which is what keeps free-form diagrams out of it.
 */

export type DiagramKind = "bpmn" | "freeform";

export const BPMN_EXTENSION = ".bpmn";

/**
 * Excalidraw's own extension, rather than something bespoke, so a file exported
 * from here opens directly in excalidraw.com and vice versa.
 */
export const FREEFORM_EXTENSION = ".excalidraw";

export const DIAGRAM_EXTENSIONS = [BPMN_EXTENSION, FREEFORM_EXTENSION] as const;

/** Extensions accepted by the upload picker and drag-and-drop. */
export const UPLOAD_ACCEPT = DIAGRAM_EXTENSIONS.join(",");

/**
 * Returns the kind for a path, or null when the file is neither — an uploaded
 * SVG sidecar, for example. Callers decide what to do with an unknown file;
 * the tree still lists it, it just has no editor.
 */
export function diagramKindOf(filePath: string): DiagramKind | null {
  const lower = filePath.toLowerCase();
  if (lower.endsWith(BPMN_EXTENSION)) return "bpmn";
  if (lower.endsWith(FREEFORM_EXTENSION)) return "freeform";
  return null;
}

export const isFreeForm = (filePath: string) => diagramKindOf(filePath) === "freeform";
export const isBpmn = (filePath: string) => diagramKindOf(filePath) === "bpmn";

export function extensionFor(kind: DiagramKind): string {
  return kind === "bpmn" ? BPMN_EXTENSION : FREEFORM_EXTENSION;
}

/** Appends the right extension unless the name already carries it. */
export function withExtension(name: string, kind: DiagramKind): string {
  const extension = extensionFor(kind);
  return name.toLowerCase().endsWith(extension) ? name : `${name}${extension}`;
}

/** Strips a known diagram extension for display. */
export function stripExtension(fileName: string): string {
  for (const extension of DIAGRAM_EXTENSIONS) {
    if (fileName.toLowerCase().endsWith(extension)) {
      return fileName.slice(0, -extension.length);
    }
  }
  return fileName;
}

export const KIND_LABEL: Record<DiagramKind, string> = {
  bpmn: "BPMN diagram",
  freeform: "Free-form diagram",
};
